import { useEffect, useMemo, useRef } from "react";
import { useMap, useMapEvents } from "react-leaflet";
import type { VillageViewModel } from "../../../domain/contracts";
import L from "leaflet";

export function MapClickReset({ onClear }: { onClear: () => void }) {
  useMapEvents({ click() { onClear(); } });
  return null;
}

export function MapCenterUpdater({ panTarget, zoom }: { panTarget: [number, number] | null; zoom?: number | null }) {
  const map = useMap();
  useEffect(() => {
    if (!panTarget) return;
    const targetZoom = zoom ?? map.getZoom();
    map.setView(panTarget, targetZoom, { animate: true });
  }, [map, panTarget, zoom]);
  return null;
}

export const LABEL_MIN_ZOOM = 14;
const MARKER_BASE_SIZE = 20;
const LABEL_BASE_SIZE = 9;

function scaleForZoom(zoom: number): number {
  const raw = Math.max(0.5, Math.min(2.4, (zoom - 4) * 0.17));
  return Math.round(raw * 100) / 100;
}

export function MapVillageOverlay({
  villages,
  onMarkerClick,
  onMarkerHover,
  onMarkerLeave,
}: {
  villages: VillageViewModel[];
  onMarkerClick: (village: VillageViewModel) => void;
  onMarkerHover: (villageId: string) => void;
  onMarkerLeave: (villageId: string) => void;
}) {
  const map = useMap();
  const markersRef = useRef<L.Marker[]>([]);
  const labelMarkersRef = useRef<L.Marker[]>([]);
  const lastZoomRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clickRef = useRef(onMarkerClick);
  clickRef.current = onMarkerClick;
  const hoverRef = useRef(onMarkerHover);
  hoverRef.current = onMarkerHover;
  const leaveRef = useRef(onMarkerLeave);
  leaveRef.current = onMarkerLeave;

  const visible = useMemo(
    () => villages.filter((v) => v.coordinates.lat !== 0 || v.coordinates.lng !== 0),
    [villages],
  );

  // Create markers once on mount / villages change — never recreate on zoom
  useEffect(() => {
    markersRef.current.forEach((m) => m.remove());
    labelMarkersRef.current.forEach((m) => m.remove());
    markersRef.current.length = 0;
    labelMarkersRef.current.length = 0;
    lastZoomRef.current = 0;

    for (const village of visible) {
      const isPreviewBacked = Boolean(village.backendRef) && village.stats.interviewCount > 0;
      const markerClass = isPreviewBacked ? "map-marker-village" : "map-marker-village-no-data";

      const iconHtml = `<span class="${markerClass}" style="width:${MARKER_BASE_SIZE}px;height:${MARKER_BASE_SIZE}px"></span>`;
      const icon = L.divIcon({
        className: "map-marker-shell",
        html: iconHtml,
        iconSize: [MARKER_BASE_SIZE, MARKER_BASE_SIZE],
        iconAnchor: [MARKER_BASE_SIZE / 2, MARKER_BASE_SIZE / 2],
      });

      const marker = L.marker([village.coordinates.lat, village.coordinates.lng], {
        icon,
        interactive: true,
      })
        .on("click", () => clickRef.current(village))
        .on("mouseover", () => hoverRef.current(village.id))
        .on("mouseout", () => leaveRef.current(village.id))
        .addTo(map);

      markersRef.current.push(marker);

      const labelIcon = L.divIcon({
        className: "map-village-label",
        html: `<span>${village.title}</span>`,
        iconSize: [140, 16],
        iconAnchor: [70, -(MARKER_BASE_SIZE / 2 + 8)],
      });
      const labelMarker = L.marker([village.coordinates.lat, village.coordinates.lng], {
        icon: labelIcon,
        interactive: false,
        keyboard: false,
      }).addTo(map);
      labelMarkersRef.current.push(labelMarker);
    }

    applyZoomScale(map, map.getZoom());
  }, [map, visible]);

  // Debounced zoom handler — CSS-only scaling, no DOM rebuild
  useEffect(() => {
    function onZoom() {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        const zoom = map.getZoom();
        if (Math.abs(zoom - lastZoomRef.current) < 0.5) return;
        lastZoomRef.current = zoom;
        applyZoomScale(map, zoom);
      }, 100);
    }
    map.on("zoomend", onZoom);
    return () => {
      map.off("zoomend", onZoom);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [map]);

  return null;
}

function applyZoomScale(map: L.Map, zoom: number) {
  const scale = scaleForZoom(zoom);
  const showLabels = zoom >= LABEL_MIN_ZOOM;
  map.getContainer().style.setProperty("--marker-scale", String(scale));
  map.getContainer().style.setProperty("--village-label-size", `${Math.round(LABEL_BASE_SIZE * scale)}px`);
  map.getContainer().style.setProperty("--village-label-opacity", showLabels ? "1" : "0");
}